import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ShieldCheck, TrendingUp, Clock, Layers } from 'lucide-react';
interface SlideAIOpportunityProps {
  onContinue: () => void;
}
export function SlideAIOpportunity({
  onContinue
}: SlideAIOpportunityProps) {
  const [stage, setStage] = useState(0);
  const pillars = [{
    icon: <ShieldCheck className="w-6 h-6" data-id="element-76" />,
    title: 'Reduce the Erosion',
    desc: 'Continuous, data-driven engagement keeps HCPs connected to the brand — actively defending market share instead of letting it fade.',
    metric: 'Slow decline from ~25% to ~10% per year',
    tint: 'from-blue-100/70 to-blue-50/40',
    iconBg: 'bg-blue-100/80',
    iconText: 'text-blue-500'
  }, {
    icon: <TrendingUp className="w-6 h-6" data-id="element-77" />,
    title: 'Increase Profitability',
    desc: 'AI-automated marketing operates at a fraction of the cost of traditional sales and agency models — dramatically widening the margin on every euro of revenue.',
    metric: 'Marketing cost reduced by up to 80%',
    tint: 'from-violet-100/70 to-violet-50/40',
    iconBg: 'bg-violet-100/80',
    iconText: 'text-[#6C2BD9]'
  }, {
    icon: <Clock className="w-6 h-6" data-id="element-78" />,
    title: 'Prolong the Lifespan',
    desc: 'Lower costs + slower erosion means the gap between revenue and cost stays positive for years longer. Products stay on market generating value, instead of being withdrawn prematurely.',
    metric: 'Years of additional profitable life',
    tint: 'from-teal-100/70 to-teal-50/40',
    iconBg: 'bg-teal-100/80',
    iconText: 'text-teal-600'
  }];
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 200);
    const t2 = setTimeout(() => setStage(2), 600);
    const t3 = setTimeout(() => setStage(3), 1000);
    const t4 = setTimeout(() => setStage(4), 1400);
    const timers = pillars.map((_, idx) => setTimeout(() => setStage(5 + idx), 1700 + idx * 250));
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      timers.forEach(clearTimeout);
    };
  }, []);
  return <div className="h-full w-full flex flex-col items-center px-8 py-10 md:px-12 md:py-14 max-w-6xl mx-auto overflow-y-auto text-center" data-id="element-79">
      {/* Icon */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8,
      y: -10
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      scale: stage >= 1 ? 1 : 0.8,
      y: stage >= 1 ? 0 : -10
    }} transition={{
      duration: 0.5
    }} className="mb-8" data-id="element-80">
        <div className="w-14 h-14 rounded-2xl bg-[#6C2BD9]/10 border border-[#6C2BD9]/30 flex items-center justify-center" data-id="element-81">
          <Layers className="w-6 h-6 text-[#6C2BD9]" strokeWidth={2.2} data-id="element-82" />
        </div>
      </motion.div>

      {/* Eyebrow */}
      <motion.span initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 8
    }} transition={{
      duration: 0.4
    }} className="text-[#6C2BD9] text-xs md:text-sm uppercase tracking-[0.3em] font-bold mb-5" data-id="element-83">
        The Approach
      </motion.span>

      {/* Serif intro */}
      <motion.h3 initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: stage >= 2 ? 1 : 0,
      y: stage >= 2 ? 0 : 12
    }} transition={{
      duration: 0.5,
      delay: 0.1
    }} className="font-serif text-2xl md:text-4xl text-slate-900 leading-tight mb-2" data-id="element-84">
        Our approach unlocks
      </motion.h3>

      {/* Hero */}
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 3 ? 1 : 0,
      y: stage >= 3 ? 0 : 20
    }} transition={{
      duration: 0.6
    }} className="text-5xl md:text-7xl lg:text-8xl font-sans font-bold text-slate-900 leading-[1.02] tracking-tight" data-id="element-85">
        Three Levers
      </motion.h2>

      {/* Gradient divider */}
      <motion.div initial={{
      width: 0,
      opacity: 0
    }} animate={{
      width: stage >= 4 ? 180 : 0,
      opacity: stage >= 4 ? 1 : 0
    }} transition={{
      duration: 0.7
    }} className="h-[3px] bg-gradient-to-r from-blue-400 via-[#6C2BD9] to-teal-400 rounded-full mt-8 mb-10" data-id="element-86" />

      {/* Subtitle */}
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} transition={{
      duration: 0.5
    }} className="text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed mb-10" data-id="element-87">
        By replacing traditional marketing with AI-optimized engagement, we
        change the economics of every brand —{' '}
        <span className="text-[#6C2BD9] font-semibold" data-id="element-88">entirely.</span>
      </motion.p>

      {/* Three pillars as pill cards */}
      <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5" data-id="element-89">
        {pillars.map((pillar, idx) => <motion.div key={idx} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: stage >= 5 + idx ? 1 : 0,
        y: stage >= 5 + idx ? 0 : 20
      }} transition={{
        duration: 0.5
      }} className={`relative bg-gradient-to-br ${pillar.tint} border-2 border-[#6C2BD9]/15 rounded-3xl p-6 flex flex-col items-center text-center`} data-id="element-90">
            <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/80 border border-[#6C2BD9]/20 flex items-center justify-center" data-id="element-91">
              <span className="text-[#6C2BD9] font-bold text-xs" data-id="element-92">
                {idx + 1}
              </span>
            </div>
            <div className={`w-12 h-12 rounded-2xl ${pillar.iconBg} flex items-center justify-center ${pillar.iconText} mb-4`} data-id="element-93">
              {pillar.icon}
            </div>
            <h3 className="font-serif text-xl md:text-2xl text-slate-900 mb-3" data-id="element-94">
              {pillar.title}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed flex-1" data-id="element-95">
              {pillar.desc}
            </p>
            <div className="mt-4 pt-4 border-t border-[#6C2BD9]/15 w-full" data-id="element-96">
              <span className={`${pillar.iconText} font-semibold text-xs`} data-id="element-97">
                {pillar.metric}
              </span>
            </div>
          </motion.div>)}
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: stage >= 7 ? 1 : 0,
      y: stage >= 7 ? 0 : 8
    }} transition={{
      delay: 0.2
    }} className="mt-10 flex flex-col md:flex-row items-center justify-between w-full gap-4" data-id="element-98">
        <p className="text-slate-500 text-sm max-w-lg text-left" data-id="element-99">
          Combined, these three effects compound — each euro becomes more
          profitable and lasts longer.
        </p>
        <Button onClick={onContinue} data-id="element-100">Strategy</Button>
      </motion.div>
    </div>;
}